import React from "react";
import { useSearchParams } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { getPrivacy, setPrivacy } from "@/services/give-api";

type State = "loading" | "ready" | "saving" | "saved" | "error";

export function AccountPrivacy() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = React.useState<State>("loading");
  const [error, setError] = React.useState<string | null>(null);
  const [displayName, setDisplayName] = React.useState<string>("");
  const [consent, setConsent] = React.useState<"anonymous" | "named">("anonymous");
  const [donorDisplay, setDonorDisplay] = React.useState<string>("");

  React.useEffect(() => {
    if (!token) {
      setState("error");
      setError("No token in the URL. Use the link in your most-recent receipt email.");
      return;
    }
    (async () => {
      try {
        const data = await getPrivacy(token);
        setDonorDisplay(data.displayName || data.donorId);
        setConsent(data.recognitionConsent === "named" ? "named" : "anonymous");
        setDisplayName(data.recognitionDisplayName || "");
        setState("ready");
      } catch (err) {
        setState("error");
        setError(String(err));
      }
    })();
  }, [token]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setState("saving");
    setError(null);
    try {
      await setPrivacy({
        token,
        recognitionConsent: consent,
        recognitionDisplayName: consent === "named" ? displayName : undefined,
      });
      setState("saved");
    } catch (err) {
      setState("error");
      setError(String(err));
    }
  }

  return (
    <PageShell
      title="Recognition preference"
      subtitle="Your gift, your privacy. The Foundation never overrides this choice."
      stewardship="Account · privacy"
    >
      <Seo title="Recognition preference" description="" path="/account/privacy" noindex />
      <div className="max-w-xl">
        {state === "loading" && (
          <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">Loading…</p>
        )}

        {state === "error" && (
          <div className="border border-state-pending bg-paper-2 p-4 font-mono text-xs text-state-pending leading-relaxed">
            {error}
          </div>
        )}

        {(state === "ready" || state === "saving" || state === "saved") && (
          <form onSubmit={onSave} className="space-y-6">
            <p className="font-mono text-xs text-ink-3">
              Signed in as <span className="text-ink">{donorDisplay}</span>.
            </p>
            <fieldset className="border border-hairline p-5 bg-paper-2 space-y-3 text-sm font-mono text-ink-2">
              <legend className="font-mono text-[10px] uppercase tracking-widest text-claude font-bold px-2">
                Recognition
              </legend>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="consent"
                  checked={consent === "named"}
                  onChange={() => setConsent("named")}
                  className="mt-1"
                />
                <span className="flex-1">
                  Named —{" "}
                  <input
                    type="text"
                    placeholder="Display name"
                    disabled={consent !== "named"}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="border-b border-hairline bg-paper px-1 py-0.5 font-mono text-sm text-ink focus:outline-none focus:border-ink ml-1 w-56"
                  />
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="consent"
                  checked={consent === "anonymous"}
                  onChange={() => setConsent("anonymous")}
                  className="mt-1"
                />
                <span>Anonymous</span>
              </label>
            </fieldset>

            <button
              type="submit"
              disabled={state === "saving"}
              className="bg-ink text-paper px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-ink-2 disabled:opacity-60"
            >
              {state === "saving" ? "Saving…" : "Save preference"}
            </button>

            {state === "saved" && (
              <p className="font-mono text-xs text-claude">Saved. Thank you.</p>
            )}
          </form>
        )}
      </div>
    </PageShell>
  );
}
